import {
  AfterViewInit, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {OfferAddons} from '../../../../../app-shared/models/steps/step-offer-extras.interface';

@Component({
  selector: 'sb-addons-select',
  templateUrl: './addons-select.component.html',
  styleUrls: ['./addons-select.component.scss']
})
export class AddonsSelectComponent implements AfterViewInit {
  @Input() selectedAddons: Set<string> = new Set();
  @Input() availableAddons: OfferAddons[] = [];
  @Input() formGroup: FormGroup;
  @Input() adobeDamUrl: string;
  @Input() incompatibilities: { [key: string]: string[] } = {};
  @Output() addonsToggle: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('carouselContainer', {static: false}) carouselContainer: ElementRef;

  selected: boolean;
  public buttonLabel = 'Acquista';
  public Number = Number;
  isExpanded: boolean[] = [];
  hardCodedPreviousPrice = 1.49;
  activeIndex = 0;
  isDesktop = false;
  isMobile = false;
  showLeftShadow = false;
  showRightShadow = true;
  leftButtonDisabled = true;
  rightButtonDisabled = false;
  private touchStartX = 0;
  private touchEndX = 0;
  private isSwiping = false;

  constructor(private cdr: ChangeDetectorRef) {
    this.checkScreenSize();
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.forceCarouselUpdate();
      }, 100);
    });
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
    this.setupTouchListeners();
    this.checkScreenSize();
    this.updateAddonStates();

    setTimeout(() => {
      this.initializeCarousel();
      this.forceCarouselUpdate();
      this.updateShadowHeight();

      setTimeout(() => {
        this.forceCarouselUpdate();
      }, 500);
    }, 0);

    this.carouselContainer.nativeElement.addEventListener('scroll', () => {
      this.updateCarouselState();
      this.forceCarouselUpdate();
    });

    window.addEventListener('load', () => {
      setTimeout(() => {
        this.updateShadowHeight();
        this.forceCarouselUpdate();
      }, 100);
    });
  }

  private isCarouselOverflowing(): boolean {
    const container = this.carouselContainer.nativeElement;
    return Math.round(container.scrollWidth) > Math.round(container.clientWidth);
  }

  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.checkScreenSize();
    setTimeout(() => {
      this.forceCarouselUpdate();
      this.updateShadowHeight();
    }, 100);
  }

  checkScreenSize() {
    this.isDesktop = window.innerWidth >= 768;
    this.isMobile = !this.isDesktop;
  }

  initializeCarousel() {
    const container = this.carouselContainer.nativeElement;
    const firstItem = container.querySelector('.addon-item');
    if (firstItem) {
      container.scrollLeft = 0;
      this.activeIndex = 0;
    }
    this.forceCarouselUpdate();
    this.cdr.detectChanges();
  }

  private checkOverflow() {
    const isOverflowing = this.isCarouselOverflowing();
    this.showRightShadow = isOverflowing;
    this.rightButtonDisabled = !isOverflowing;
    this.cdr.detectChanges();
  }

  private updateShadowHeight() {
    if (this.isDesktop) {
      const containers = document.querySelectorAll('.addon-container');
      if (containers.length > 0) {
        const addonContainer = containers[0] as HTMLElement;
        const height = `${addonContainer.offsetHeight}px`;
        const shadowElements = document.querySelectorAll('.carousel-shadow');
        shadowElements.forEach((shadow: HTMLElement) => {
          shadow.style.height = height;
        });
      }
    }
  }

  checkButtonStates() {
    const container = this.carouselContainer.nativeElement;
    const scrollPosition = Math.round(container.scrollLeft);
    const scrollWidth = Math.round(container.scrollWidth);
    const clientWidth = Math.round(container.clientWidth);
    const maxScroll = scrollWidth - clientWidth;
    const isOverflowing = scrollWidth > clientWidth;

    this.leftButtonDisabled = scrollPosition <= 0;
    this.rightButtonDisabled = !isOverflowing || scrollPosition >= maxScroll;
    this.showLeftShadow = !this.leftButtonDisabled;
    this.showRightShadow = !this.rightButtonDisabled;
    this.cdr.detectChanges();
  }

  updateActiveIndex() {
    const container = this.carouselContainer.nativeElement;
    const items = container.querySelectorAll('.addon-item');
    const containerLeft = container.scrollLeft;
    const containerWidth = container.clientWidth;
    const containerCenter = containerLeft + (containerWidth / 2);

    let closestIndex = 0;
    let closestDistance = Infinity;

    items.forEach((item, index) => {
      const itemCenter = item.offsetLeft + (item.offsetWidth / 2);
      const distance = Math.abs(containerCenter - itemCenter);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    if (this.activeIndex !== closestIndex) {
      this.activeIndex = closestIndex;
      this.cdr.detectChanges();
    }
  }

  public isSelected(addonId: string): boolean {
    const isAddonSelected = this.selectedAddons.has(addonId);
    this.buttonLabel = isAddonSelected ? 'Elimina' : 'Acquista';
    return isAddonSelected;
  }

  public isDisabled(addonId: string): boolean {
    if (!this.incompatibilities) {
      return false;
    }

    const incompatibleAddons = this.incompatibilities[addonId] || [];
    return incompatibleAddons.some(incompatibleId => this.selectedAddons.has(incompatibleId));
  }

  public toggle(addonId: string) {
    if (this.isDisabled(addonId)) {
      return;
    }

    const isSelected = this.isSelected(addonId);

    if (isSelected) {
      this.selectedAddons.delete(addonId);
    } else {
      this.selectedAddons.add(addonId);
    }

    this.formGroup.patchValue({[addonId]: !isSelected});
    this.addonsToggle.emit({addonId, toggle: !isSelected});
    this.updateAddonStates();
  }

  private updateAddonStates() {
    if (!this.availableAddons || !this.incompatibilities) {
      return;
    }

    this.availableAddons.forEach(addon => {
      const isDisabled = this.isDisabled(addon.id);
      const formControl = this.formGroup.get(addon.id);
      if (formControl) {
        if (isDisabled) {
          formControl.disable();
        } else {
          formControl.enable();
        }
      }
    });
    this.cdr.detectChanges();
  }

  public shouldShowPromotionLabel(addon: OfferAddons, checkActivationPrice?: boolean) {
    const priceToCheck = addon && (checkActivationPrice ? addon.activationPrice : addon.price);
    return priceToCheck && !priceToCheck.value && priceToCheck.value === 0;
  }

  public shouldShowSpecialCard(addon: OfferAddons) {
    return addon && addon.price && addon.price.repetitions >= 1;
  }

  public inferTitleBasedOnRepetitions(addon: OfferAddons) {
    if (addon && addon.price && addon.price.repetitions > 0) {
      return `ENTITY.OFFER-EXTRAS.ADDONS.${addon.price.repetitions}-REPETITION-TITLE`;
    }
  }

  public inferSubtitleBasedOnRepetitions(addon: OfferAddons) {
    if (addon && addon.price && addon.price.repetitions > 0) {
      return `ENTITY.OFFER-EXTRAS.ADDONS.${addon.price.repetitions}-MONTH-FREE-SPECIAL-CARD`;
    }
  }

  public showActivationPricePrevious(addon: OfferAddons) {
    return addon && addon.activationPrice && (addon.activationPrice.previous > 0) ? addon.activationPrice.previous : '';
  }

  public showActivationPriceValue(addon: OfferAddons) {
    return (addon && addon.activationPrice && addon.activationPrice.value) ? addon.activationPrice.value : '';
  }

  public showActivationPriceCurrency(addon: OfferAddons) {
    return (addon && addon.activationPrice && addon.activationPrice.value > 0 ) ? addon.activationPrice.currency : '';
  }

  toggleClass(index: number) {
    this.isExpanded[index] = !this.isExpanded[index];
  }

  onMouseWheel(event: WheelEvent) {
    event.preventDefault();
    const container = this.carouselContainer.nativeElement;
    const scrollAmount = 100;
    container.scrollLeft += event.deltaY > 0 ? scrollAmount : -scrollAmount;
    this.updateCarouselState();
  }

  setupTouchListeners() {
    const container = this.carouselContainer.nativeElement;
    container.addEventListener('touchstart', (e: TouchEvent) => this.onTouchStart(e), { passive: true });
    container.addEventListener('touchmove', (e: TouchEvent) => this.onTouchMove(e), { passive: false });
    container.addEventListener('touchend', (e: TouchEvent) => this.onTouchEnd(e), { passive: true });
  }

  onTouchStart(e: TouchEvent) {
    this.touchStartX = e.touches[0].clientX;
    this.isSwiping = false;
  }

  onTouchMove(e: TouchEvent) {
    if (this.isMobile) {
      const currentX = e.touches[0].clientX;
      const diff = this.touchStartX - currentX;
      if (Math.abs(diff) > 5) {
        this.isSwiping = true;
        e.preventDefault();
      }
    }
  }

  onTouchEnd(e: TouchEvent) {
    if (this.isMobile && this.isSwiping) {
      this.touchEndX = e.changedTouches[0].clientX;
      this.handleSwipe();
    }
  }

  handleSwipe() {
    const swipeThreshold = 50; // x sensibilità dello swipe
    const diff = this.touchStartX - this.touchEndX;
    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.scrollCarousel(Math.min(this.activeIndex + 1, this.availableAddons.length - 1));
      } else {
        this.scrollCarousel(Math.max(this.activeIndex - 1, 0));
      }
      this.updateCarouselState();
    } else {
      this.scrollCarousel(this.activeIndex);
      this.updateCarouselState();
    }
    this.updateCarouselState();
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const index = Array.from(this.carouselContainer.nativeElement.children).indexOf(entry.target);
          if (this.activeIndex !== index) {
            this.activeIndex = index;
            this.cdr.detectChanges();
          }
        }
      });
    }, options);

    const addonElements = this.carouselContainer.nativeElement.querySelectorAll('.addon-item');
    addonElements.forEach(element => observer.observe(element));
  }

  isDotActive(index: number): boolean {
    return this.activeIndex === index;
  }

  scrollCarousel(index: number) {
    const container = this.carouselContainer.nativeElement;
    const addonElements = container.querySelectorAll('.addon-item');
    if (addonElements[index]) {
      const scrollLeft = addonElements[index].offsetLeft;

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth'
      });

      this.activeIndex = index;

      setTimeout(() => {
        this.checkOverflow();
        this.updateActiveIndex();
        this.cdr.detectChanges();
      }, 300);
    }
  }

  scrollLeft() {
    if (this.isDesktop && !this.leftButtonDisabled) {
      const container = this.carouselContainer.nativeElement;
      const scrollAmount = container.clientWidth;
      container.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
      this.updateCarouselState();
    }
    this.updateCarouselState();
  }

  scrollRight() {
    if (this.isDesktop && !this.rightButtonDisabled) {
      const container = this.carouselContainer.nativeElement;
      const scrollAmount = container.clientWidth;
      const maxScrollLeft = container.scrollWidth - container.clientWidth;
      const newScrollPosition = Math.min(container.scrollLeft + scrollAmount, maxScrollLeft);

      container.scrollTo({
        left: newScrollPosition,
        behavior: 'smooth'
      });
      this.updateCarouselState();
    }
    this.updateCarouselState();
  }

  private updateCarouselState() {
    const container = this.carouselContainer.nativeElement;
    const scrollPosition = Math.round(container.scrollLeft);
    const scrollWidth = Math.round(container.scrollWidth);
    const clientWidth = Math.round(container.clientWidth);
    const maxScroll = scrollWidth - clientWidth;

    this.showLeftShadow = scrollPosition > 0;
    this.showRightShadow = scrollPosition < maxScroll;

    this.leftButtonDisabled = scrollPosition <= 0;
    this.rightButtonDisabled = scrollPosition >= maxScroll;

    this.checkButtonStates();
    this.updateActiveIndex();
    this.cdr.detectChanges();
  }

  private forceCarouselUpdate() {
    const isOverflowing = this.isCarouselOverflowing();
    if (isOverflowing) {
      this.rightButtonDisabled = false;
      this.showRightShadow = true;
    } else {
      this.rightButtonDisabled = true;
      this.showRightShadow = false;
    }

    this.updateCarouselState();
    this.updateShadowHeight();
    this.cdr.detectChanges();
  }
}
